import { apiClient } from '@app/api';
import { useIsAdminUser } from '@app/core/auth/authentication/hooks/useIsAdminUser';
import { useAuth0 } from '@auth0/auth0-react';
import { useLayoutEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useImpersonation } from '../../impersonation';
import {
  AUTH_AS_QUERY_PARAM,
  captureTabAuthAsFromSearch,
  clearTabAuthAs,
  stripAuthAsFromSearch,
} from '../../impersonation/tab-auth-as';

/**
 * Applies impersonation headers on the shared API client before app-init runs.
 *
 * Precedence for the Mediainbox admin Auth0 identity:
 * 1. Tab-scoped `auth_as` query param / sessionStorage (backoffice deep links)
 * 2. Persisted impersonated user from the impersonation store
 *
 * The query param is stored in sessionStorage (this tab only) and stripped from the URL.
 */
export function useImpersonationInit() {
  const isAdminUser = useIsAdminUser();
  const { user: auth0User } = useAuth0();
  const history = useHistory();
  const location = useLocation();
  const strippedSearchRef = useRef<string | null>(null);

  // Persist ?auth_as= before useImpersonation reads sessionStorage.
  captureTabAuthAsFromSearch(location.search);
  const { authAsUserId, tabAuthAsUserId } = useImpersonation();
  const resolvedAuthAs = authAsUserId ?? null;

  if (!auth0User || isAdminUser) {
    // Before Auth0 identity is known, still apply so deep links do not race app-init.
    // Backend enforces that only SUPERADMIN may use x-auth-as.
    if (apiClient.authAs !== resolvedAuthAs) {
      apiClient.setAuthAs(resolvedAuthAs);
    }
  } else if (tabAuthAsUserId) {
    // Known non-admin: drop the tab override and never send x-auth-as.
    clearTabAuthAs();
    if (apiClient.authAs) {
      apiClient.setAuthAs(null);
    }
  }

  // Strip auth_as from the address bar once after capture (keeps react-router in sync).
  useLayoutEffect(() => {
    const params = new URLSearchParams(
      location.search.startsWith('?') ? location.search.slice(1) : location.search,
    );
    if (!params.has(AUTH_AS_QUERY_PARAM)) {
      return;
    }
    if (strippedSearchRef.current === location.search) {
      return;
    }
    strippedSearchRef.current = location.search;
    history.replace({
      pathname: location.pathname,
      search: stripAuthAsFromSearch(location.search),
      hash: location.hash,
      state: location.state,
    });
  }, [history, location.hash, location.pathname, location.search, location.state]);

  useLayoutEffect(() => {
    if (auth0User && !isAdminUser) {
      return;
    }
    const nextAuthAs = authAsUserId ?? null;
    if (apiClient.authAs !== nextAuthAs) {
      apiClient.setAuthAs(nextAuthAs);
    }
  }, [auth0User, authAsUserId, isAdminUser]);
}
